import { FINPSP_DEPOSIT_TYPE } from '../../../constants/finpsp';
import { Box, Divider, Stack, Typography } from '@mui/material';
import CopyButton from '../../atoms/CopyButton';
import { getFinpspDepositType, isFinpspTerminal } from '../../../helpers/payment/finpsp';
import useLocalization from '../../../hooks/useLocalization';
import type { FinpspPayment } from '../../../types/finpsp';
import FinpspReceivingNumberWaiting from './FinpspReceivingNumberWaiting';

const styles = {
  layout: {
    gap: 2,
  },
  root: {
    m: 0,
    px: 2.5,
    borderRadius: 3,
    bgcolor: 'neutral.600',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 2,
  },
  label: {
    mb: 1,
    fontWeight: 600,
  },
  value: {
    m: 0,
    typography: 'h2',
    letterSpacing: 0,
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'anywhere',
  },
  copy: {
    color: 'primary.main',
    minWidth: 44,
    minHeight: 44,
    px: 1.5,
    flexShrink: 0,
  },
};

type PaymentDetailProps = {
  label: string;
  value: string;
  copyLabel: string;
  onCopy?: () => Promise<boolean>;
};

const PaymentDetail = ({ label, value, copyLabel, onCopy }: PaymentDetailProps) => (
  <Box sx={styles.row}>
    <Box>
      <Typography component='dt' variant='caption' color='text.primary' sx={styles.label}>
        {label}
      </Typography>
      <Box component='dd' sx={styles.value}>
        {value}
      </Box>
    </Box>
    {onCopy && <CopyButton sx={styles.copy} copyLabel={copyLabel} onCopy={onCopy} />}
  </Box>
);

type FinpspPaymentDetailsProps = {
  payment: FinpspPayment;
  onCopy: (value: string) => Promise<boolean>;
};

const FinpspPaymentDetails = ({ payment, onCopy }: FinpspPaymentDetailsProps) => {
  const { t } = useLocalization();
  const editable = !isFinpspTerminal(payment);
  const showReceivingNumber = editable && getFinpspDepositType(payment) !== FINPSP_DEPOSIT_TYPE.P2C;

  const receivingNumber = payment.sim_number?.trim() ?? '';
  const waitingForNumber = showReceivingNumber && !receivingNumber;

  return (
    <Stack direction='column' sx={styles.layout}>
      {waitingForNumber && <FinpspReceivingNumberWaiting />}
      <Box component='dl' sx={styles.root}>
        {showReceivingNumber && receivingNumber && (
          <>
            <PaymentDetail
              label={t('finpspReceivingNumber')}
              value={receivingNumber}
              copyLabel={t('finpspCopyNumber')}
              onCopy={() => onCopy(receivingNumber)}
            />
            <Divider />
          </>
        )}
        <PaymentDetail
          label={t('amount')}
          value={`${payment.amount.toString()} ${payment.currency}`}
          copyLabel={t('finpspCopyAmount')}
          onCopy={editable ? () => onCopy(payment.amount.toString()) : undefined}
        />
      </Box>
    </Stack>
  );
};

export default FinpspPaymentDetails;
